import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import BreadCrumb from "Common/BreadCrumb";
import {
  getMerchantLotterySlots,
  getVasIntegrationInfo,
  patchMerchantServiceRule,
  VasMerchantIntegrationDto,
} from "services/apiMerchantVas";
import {
  getTriggerWebhook,
  saveTriggerWebhook,
} from "services/apiMerchantTriggerWebhook";
import { errMsg, normalizeWebhookUrl } from "Common/errMsg";

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const display = value || "—";

  const onCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mb-3">
      <div className="text-xs uppercase text-slate-500 dark:text-zink-400 mb-1">
        {label}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <code className="text-sm bg-slate-100 dark:bg-zink-700 px-2 py-1 rounded break-all">
          {display}
        </code>
        {value ? (
          <button
            type="button"
            onClick={onCopy}
            className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Merchant-only portal: shows this merchant's VAS ruleConfigId / serviceId,
 * contact details, and lottery slots for partner integrations.
 */
const MerchantApi = () => {
  const queryClient = useQueryClient();
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [entryWindowHours, setEntryWindowHours] = useState("4");
  const [entryCloseMinutes, setEntryCloseMinutes] = useState("60");
  const [numbersToSelect, setNumbersToSelect] = useState("5");
  const [numberRange, setNumberRange] = useState("50");

  useEffect(() => {
    document.title = "API & VAS | Betaplay Merchant";
  }, []);

  const {
    data: vas,
    isLoading: vasLoading,
    error: vasError,
    refetch,
  } = useQuery<VasMerchantIntegrationDto>({
    queryKey: ["vas-integration-info"],
    queryFn: getVasIntegrationInfo,
    retry: false,
  });

  const webhookQuery = useQuery({
    queryKey: ["merchant-trigger-webhook"],
    queryFn: getTriggerWebhook,
    retry: false,
  });

  useEffect(() => {
    const fromApi = webhookQuery.data;
    const fromVas = vas;
    if (fromApi) {
      setWebhookUrl(fromApi.triggerWebhookUrl || "");
      setWebhookEnabled(Boolean(fromApi.triggerWebhookEnabled));
    } else if (fromVas && (fromVas.triggerWebhookUrl != null || fromVas.triggerWebhookEnabled != null)) {
      setWebhookUrl(fromVas.triggerWebhookUrl || "");
      setWebhookEnabled(Boolean(fromVas.triggerWebhookEnabled));
    }
  }, [webhookQuery.data, vas]);

  useEffect(() => {
    if (!vas) return;
    setServiceId((vas.serviceId || vas.mappedServiceEnumId || "").toUpperCase());
    const triggerMins = vas.entryWindowTriggerMinutesBeforeDraw;
    if (triggerMins != null && triggerMins > 0) {
      setEntryWindowHours(String(Math.round(triggerMins / 60)));
    }
    if (vas.entryCloseMinutesBeforeDraw != null) {
      setEntryCloseMinutes(String(vas.entryCloseMinutesBeforeDraw));
    }
    if (vas.numbersToSelect != null) {
      setNumbersToSelect(String(vas.numbersToSelect));
    }
    if (vas.numberRange != null) {
      setNumberRange(String(vas.numberRange));
    }
  }, [vas]);

  const saveWebhookMutation = useMutation({
    mutationFn: () => {
      if (webhookEnabled) {
        const normalized = normalizeWebhookUrl(webhookUrl);
        if (!normalized || !normalized.includes(".")) {
          return Promise.reject(
            "Enter a valid webhook URL, e.g. https://api.yourcompany.com/betaplay/triggers"
          );
        }
        return saveTriggerWebhook({
          triggerWebhookUrl: normalized,
          triggerWebhookEnabled: true,
        });
      }
      return saveTriggerWebhook({
        triggerWebhookUrl: webhookUrl.trim() ? normalizeWebhookUrl(webhookUrl) : "",
        triggerWebhookEnabled: false,
      });
    },
    onSuccess: (data) => {
      if (data?.triggerWebhookUrl) {
        setWebhookUrl(data.triggerWebhookUrl);
      }
      setWebhookEnabled(Boolean(data?.triggerWebhookEnabled));
      toast.success("Trigger webhook saved. New triggers will be POSTed to your URL.");
      queryClient.invalidateQueries({ queryKey: ["merchant-trigger-webhook"] });
      queryClient.invalidateQueries({ queryKey: ["vas-integration-info"] });
    },
    onError: (e: unknown) => {
      toast.error(errMsg(e, "Failed to save webhook"));
    },
  });

  const saveVasMutation = useMutation({
    mutationFn: () => {
      const hours = Number(entryWindowHours);
      const closeMins = Number(entryCloseMinutes);
      const nums = Number(numbersToSelect);
      const range = Number(numberRange);
      if (!serviceId.trim()) {
        return Promise.reject("Select a serviceId (BETATIPS, BETAVOW, or MADINA_LOTTERY).");
      }
      if (!Number.isFinite(hours) || hours < 0 || hours > 24) {
        return Promise.reject("Hours before draw must be between 0 and 24.");
      }
      if (!Number.isFinite(closeMins) || closeMins < 0) {
        return Promise.reject("Entry close minutes must be 0 or more.");
      }
      return patchMerchantServiceRule({
        serviceId: serviceId.trim().toUpperCase(),
        entryWindowTriggerMinutesBeforeDraw: Math.round(hours * 60),
        entryCloseMinutesBeforeDraw: Math.round(closeMins),
        numbersToSelect: Number.isFinite(nums) ? nums : undefined,
        numberRange: Number.isFinite(range) ? range : undefined,
      });
    },
    onSuccess: () => {
      toast.success("VAS settings saved. Ticket submissions now use this serviceId and rule config.");
      queryClient.invalidateQueries({ queryKey: ["vas-integration-info"] });
    },
    onError: (e: unknown) => {
      toast.error(errMsg(e, "Failed to save VAS settings"));
    },
  });

  const slotsQuery = useQuery({
    queryKey: ["merchant-lottery-slots"],
    queryFn: getMerchantLotterySlots,
    retry: false,
  });

  return (
    <React.Fragment>
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <BreadCrumb title="Merchant" pageTitle="API & VAS integration" />

        <div className="flex justify-end mb-4">
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded bg-slate-200 dark:bg-zink-600 hover:bg-slate-300"
            onClick={() => {
              refetch();
              slotsQuery.refetch();
              webhookQuery.refetch();
            }}
          >
            Refresh
          </button>
        </div>

        {vasLoading ? (
          <p className="text-slate-600">Loading your integration snapshot…</p>
        ) : vasError ? (
          <div className="card border border-red-200">
            <div className="card-body text-red-700">
              Could not load VAS integration info. Try logging in again, or
              confirm the API includes{" "}
              <code className="text-xs">GET /api/v1/users/vas-integration-info</code>.
            </div>
          </div>
        ) : vas ? (
          <>
            <div
              className={`card mb-4 border ${
                vas.vasIntegrationReady
                  ? "border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20"
                  : "border-amber-200 bg-amber-50/50 dark:bg-amber-950/20"
              }`}
            >
              <div className="card-body">
                <h5 className="mb-2 font-semibold">
                  {vas.vasIntegrationReady
                    ? "Your VAS configuration looks consistent"
                    : "Something needs attention"}
                </h5>
                <p className="text-sm text-slate-700 dark:text-zink-200 mb-0">
                  {vas.statusMessage}
                </p>
                <p className="text-xs text-slate-500 mt-3 mb-0">
                  Give your technical partner the exact{" "}
                  <strong>ruleConfigId</strong> and <strong>serviceId</strong> below
                  for <code className="text-xs">POST /api/v1/vas/ticket-submissions</code>.
                  Wrong values cause: &quot;Rule config not active or does not match
                  serviceId&quot;.
                </p>
              </div>
            </div>

            <div
              id="sms-trigger-webhook"
              className="card mb-4 border-2 border-blue-500 dark:border-blue-600 shadow-sm"
            >
              <div className="card-body">
                <h5 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">
                  SMS trigger webhook (required for user SMS)
                </h5>
                <p className="text-sm text-slate-600 dark:text-zink-300 mb-4">
                  When Betaplay creates a trigger (e.g.{" "}
                  <code className="text-xs">ENTRY_ACCEPTED</code> after a ticket,
                  or <code className="text-xs">RESULT_NO_WIN</code> after a draw),
                  we <strong>POST</strong> JSON to your URL below. Your service sends
                  the SMS, then calls Betaplay&apos;s ack API. Brand:{" "}
                  <code className="text-xs">{vas.serviceId || "—"}</code>.
                </p>
                {webhookQuery.isError ? (
                  <p className="text-sm text-amber-800 dark:text-amber-200 mb-3 rounded bg-amber-50 dark:bg-amber-950/40 px-3 py-2">
                    Could not load saved webhook from API (
                    <code className="text-xs">GET /merchant/trigger-webhook</code>
                    ). You can still enter a URL and save — ensure the latest Betaplay
                    API is deployed.
                  </p>
                ) : null}
                {webhookQuery.isLoading && !webhookQuery.isError ? (
                  <p className="text-sm text-slate-600 mb-3">Loading webhook settings…</p>
                ) : null}
                <label className="flex items-center gap-2 mb-3 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={webhookEnabled}
                    onChange={(e) => setWebhookEnabled(e.target.checked)}
                    className="rounded size-4"
                  />
                  Enable automatic trigger delivery to my webhook
                </label>
                <label className="block text-xs uppercase text-slate-500 mb-1 font-semibold">
                  Webhook URL (HTTPS)
                </label>
                <input
                  type="url"
                  className="w-full max-w-2xl px-3 py-2.5 text-sm border border-slate-300 dark:border-zink-500 rounded bg-white dark:bg-zink-800"
                  placeholder="https://your-service.example.com/api/betaplay/triggers"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  disabled={!webhookEnabled}
                />
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Tip: you can enter <code className="text-xs">madina.com/webhook</code> — we
                  will save it as <code className="text-xs">https://madina.com/webhook</code>.
                </p>
                <p className="text-xs text-slate-500 mt-2 mb-3">
                  Status:{" "}
                  <span
                    className={
                      webhookQuery.data?.triggerWebhookConfigured ||
                      vas.triggerWebhookConfigured
                        ? "text-emerald-700 dark:text-emerald-400 font-medium"
                        : "text-amber-700 dark:text-amber-400 font-medium"
                    }
                  >
                    {webhookQuery.data?.triggerWebhookConfigured ||
                    vas.triggerWebhookConfigured
                      ? "Configured — Betaplay will POST new triggers to this URL."
                      : "Not active — enable and save a valid URL to receive triggers."}
                  </span>
                </p>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={
                    saveWebhookMutation.isPending ||
                    (webhookEnabled && !webhookUrl.trim())
                  }
                  onClick={() => saveWebhookMutation.mutate()}
                >
                  {saveWebhookMutation.isPending ? "Saving…" : "Save webhook"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3 font-semibold">Your account</h5>
                  <CopyField label="Email" value={vas.email || ""} />
                  <CopyField
                    label="Phone (login / MSISDN)"
                    value={vas.phoneNumber || ""}
                  />
                  <div className="text-sm text-slate-600 dark:text-zink-300">
                    <span className="text-xs uppercase text-slate-500 block mb-1">
                      Username
                    </span>
                    {vas.username || "—"}
                  </div>
                  <div className="mt-3 text-sm text-slate-600 dark:text-zink-300">
                    <span className="text-xs uppercase text-slate-500 block mb-1">
                      Merchant user id (your Betaplay user id)
                    </span>
                    {vas.merchantUserId ?? "—"}
                  </div>
                  <div className="mt-2 text-sm text-slate-600 dark:text-zink-300">
                    <span className="text-xs uppercase text-slate-500 block mb-1">
                      Brand merchant id
                    </span>
                    {vas.brandMerchantId ?? "—"}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <h5 className="mb-2 font-semibold">VAS ticket submission</h5>
                  <p className="text-sm text-slate-600 dark:text-zink-300 mb-4">
                    BetaTips and BetaVow must use their own <strong>serviceId</strong> — not
                    Madina&apos;s. Saving here binds this login to that brand&apos;s rule
                    config (new <code className="text-xs">ruleConfigId</code>) and sets the
                    final-hour reminder without SQL.
                  </p>
                  <label className="block text-xs uppercase text-slate-500 mb-1 font-semibold">
                    serviceId (request body)
                  </label>
                  <select
                    className="w-full max-w-xl mb-3 px-3 py-2.5 text-sm border border-slate-300 dark:border-zink-500 rounded bg-white dark:bg-zink-800"
                    value={serviceId}
                    onChange={(e) => setServiceId(e.target.value)}
                  >
                    <option value="">Select brand…</option>
                    {(vas.availableServiceIds && vas.availableServiceIds.length
                      ? vas.availableServiceIds
                      : ["MADINA_LOTTERY", "BETATIPS", "BETAVOW"]
                    ).map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </select>
                  <CopyField
                    label="ruleConfigId (request body — assigned, not shared)"
                    value={
                      vas.ruleConfigId != null ? String(vas.ruleConfigId) : ""
                    }
                  />
                  <CopyField
                    label="Mapped service (internal)"
                    value={vas.mappedServiceEnumId || ""}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <div>
                      <label className="block text-xs uppercase text-slate-500 mb-1 font-semibold">
                        Final-hour reminder (hours before draw)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={24}
                        step={1}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded bg-white dark:bg-zink-800"
                        value={entryWindowHours}
                        onChange={(e) => setEntryWindowHours(e.target.value)}
                      />
                      <p className="text-xs text-slate-500 mt-1 mb-0">
                        4 = four hours before draw (ENTRY_WINDOW_FINAL_HOUR).
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs uppercase text-slate-500 mb-1 font-semibold">
                        Entry close (minutes before draw)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={1440}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded bg-white dark:bg-zink-800"
                        value={entryCloseMinutes}
                        onChange={(e) => setEntryCloseMinutes(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase text-slate-500 mb-1 font-semibold">
                        Numbers to select
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded bg-white dark:bg-zink-800"
                        value={numbersToSelect}
                        onChange={(e) => setNumbersToSelect(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase text-slate-500 mb-1 font-semibold">
                        Number range
                      </label>
                      <input
                        type="number"
                        min={2}
                        max={99}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded bg-white dark:bg-zink-800"
                        value={numberRange}
                        onChange={(e) => setNumberRange(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                    <div>
                      <span className="text-xs uppercase text-slate-500">
                        rule active
                      </span>
                      <div>{String(vas.ruleConfigActive ?? "—")}</div>
                    </div>
                    <div>
                      <span className="text-xs uppercase text-slate-500">
                        submissions paused
                      </span>
                      <div>{String(vas.submissionsPaused ?? "—")}</div>
                    </div>
                    <div>
                      <span className="text-xs uppercase text-slate-500">
                        shadow mode
                      </span>
                      <div>{String(vas.shadowMode ?? "—")}</div>
                    </div>
                    <div>
                      <span className="text-xs uppercase text-slate-500">
                        numbers / range (live)
                      </span>
                      <div>
                        {vas.numbersToSelect ?? "—"} / {vas.numberRange ?? "—"}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="mt-4 px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    disabled={saveVasMutation.isPending}
                    onClick={() => saveVasMutation.mutate()}
                  >
                    {saveVasMutation.isPending ? "Saving…" : "Save VAS settings"}
                  </button>
                </div>
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-body">
                <h5 className="mb-3 font-semibold">
                  Your lottery slots (<code>gameSlotId</code>)
                </h5>
                {slotsQuery.isLoading ? (
                  <p className="text-sm text-slate-600">Loading slots…</p>
                ) : slotsQuery.isError ? (
                  <p className="text-sm text-amber-800">
                    Could not load lottery slots. If this persists, contact
                    support.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-zink-600 text-left">
                          <th className="py-2 pr-3">gameSlotId</th>
                          <th className="py-2 pr-3">Title</th>
                          <th className="py-2 pr-3">State</th>
                          <th className="py-2 pr-3"># / range</th>
                          <th className="py-2 pr-3">VAS slot</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(slotsQuery.data || []).map((row) => (
                          <tr
                            key={row.id}
                            className="border-b border-slate-100 dark:border-zink-700"
                          >
                            <td className="py-2 pr-3 font-mono">{row.id}</td>
                            <td className="py-2 pr-3">
                              {row.slotTitle || row.title || "—"}
                            </td>
                            <td className="py-2 pr-3">{row.state || "—"}</td>
                            <td className="py-2 pr-3">
                              {row.numbersToSelect ?? "—"} /{" "}
                              {row.numberRange ?? "—"}
                            </td>
                            <td className="py-2 pr-3">
                              {row.isVasSlot ? "Yes" : "No"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(slotsQuery.data || []).length === 0 ? (
                      <p className="text-sm text-slate-600 mt-2">
                        No lottery slots returned yet.
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </React.Fragment>
  );
};

export default MerchantApi;
